import { useMemo, useState } from 'react';
// mantine
import {
    Combobox,
    CloseButton,
    PillsInput,
    Pill,
    ScrollArea,
    useCombobox,
    Switch,
    TextInput,
    UnstyledButton
} from '@mantine/core';
// icons
import { ChevronDown } from 'lucide-react';
// components
import { IConversationsIntegration } from '@newo/api/gateway';
import ConnectorPill from '@/modules/Integrations/components/ConnectorSelect/components/ConnectorPill';
import Integration from '@/modules/Integrations/components/ConnectorSelect/components/Integration';
// utils
import {
    buildIntegrationsDictionary,
    filterIntegrations,
    foldConnectors,
    unFoldConnectors
} from '@/modules/Integrations/components/ConnectorSelect/ConnectorSelect.utils.ts';
// assets
import styles from '@/modules/Integrations/components/ConnectorSelect/ConnectorSelect.module.css';

interface ConnectorSelectProps {
    integrations: IConversationsIntegration[];
    value?: string[];
    onChange: (value: string[]) => void;
}

const EMPTY_ARRAY: string[] = [];
const VISIBLE_CONNECTORS_NUMBER = 2;

const ConnectorSelect = ({
    integrations,
    value: selectedConnectors = EMPTY_ARRAY,
    onChange
}: ConnectorSelectProps) => {
    const combobox = useCombobox({
        onDropdownClose: () => {
            combobox.resetSelectedOption();
        },
        onDropdownOpen: () => {
            combobox.updateSelectedOptionIndex('active');
        }
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSelectedOnly, setIsSelectedOnly] = useState(false);

    // {integrationIdn: [integrationIdn/connectorIdn, ...]}
    const integrationsDictionary = useMemo(
        () => buildIntegrationsDictionary(integrations),
        [integrations]
    );

    // convert integration/* to integration/connector1, integration/connector2, ...
    const unFoldedConnectors = useMemo(
        () => unFoldConnectors(selectedConnectors, integrationsDictionary),
        [selectedConnectors, integrationsDictionary]
    );

    const values = useMemo(
        () =>
            unFoldedConnectors.map((item) => (
                <ConnectorPill key={item} value={item} />
            )),
        [unFoldedConnectors]
    );

    const handleClear = (event: React.MouseEvent) => {
        event.preventDefault();
        onChange([]);
    };

    const filteredIntegrations = useMemo(
        () =>
            filterIntegrations(
                integrations,
                selectedConnectors,
                searchTerm,
                isSelectedOnly
            ),
        [integrations, isSelectedOnly, searchTerm, selectedConnectors]
    );

    const handleChange = (connectors: string[]) => {
        onChange(foldConnectors(connectors, integrationsDictionary));
    };

    return (
        <Combobox store={combobox}>
            <Combobox.DropdownTarget>
                <PillsInput
                    label="Integration / Connector"
                    size="xs"
                    onClick={() => {
                        combobox.openDropdown();
                    }}
                >
                    <Pill.Group className={styles.group}>
                        <div className={styles.pills}>
                            {values.length > 0 ? (
                                values
                            ) : (
                                <span className="text-gray-6">
                                    Select Connectors
                                </span>
                            )}
                        </div>
                        {selectedConnectors.length >
                            VISIBLE_CONNECTORS_NUMBER && (
                            <span className={styles.counter}>
                                {`${selectedConnectors.length} selected`}
                            </span>
                        )}
                        {selectedConnectors.length > 0 ? (
                            <CloseButton
                                className={styles.icon}
                                size="xs"
                                onClick={handleClear}
                            />
                        ) : (
                            <UnstyledButton className={styles.icon}>
                                <ChevronDown />
                            </UnstyledButton>
                        )}
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown className={styles.dropdown}>
                <div className={styles.filters}>
                    <TextInput
                        className={styles.searchInput}
                        label="Search connectors"
                        placeholder="Search connector"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.currentTarget.value);
                        }}
                    />
                    <Switch
                        checked={isSelectedOnly}
                        label="Show selected"
                        size="xs"
                        onChange={(event) => {
                            setIsSelectedOnly(event.currentTarget.checked);
                        }}
                    />
                </div>
                <Combobox.Options>
                    <ScrollArea.Autosize mah={250} type="always">
                        {filteredIntegrations.map((integration) => {
                            return (
                                <Integration
                                    integration={integration}
                                    key={integration.integration.idn}
                                    values={unFoldedConnectors}
                                    onChange={handleChange}
                                />
                            );
                        })}
                    </ScrollArea.Autosize>
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

export default ConnectorSelect;
